
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

interface SEOMetadataProps {
  title: string;
  description: string;
  keywords?: string[];
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  noindex?: boolean;
}

const SEOMetadata: React.FC<SEOMetadataProps> = ({
  title,
  description,
  keywords = [],
  ogImage = '/lovable-uploads/a7814c71-1569-43b8-8a9c-e24f811be427.png',
  ogType = 'website',
  canonicalUrl,
  twitterCard = 'summary_large_image',
  noindex = false
}) => {
  const siteTitle = 'Cats About Town Tours';
  const fullTitle = `${title} | ${siteTitle}`;
  const defaultKeywords = [
    'NYC cat history',
    'feline walking tours',
    'Brooklyn Heights cats',
    'Peggy Gavan',
    'cat tours New York'
  ];
  const allKeywords = [...new Set([...defaultKeywords, ...keywords])].join(', ');
  const location = useLocation();
  
  // Generate the absolute canonical URL
  const baseUrl = 'https://catsabouttowntours.com';
  const currentPath = location.pathname;
  const absoluteCanonicalUrl = canonicalUrl ? 
    (canonicalUrl.startsWith('http') ? canonicalUrl : `${baseUrl}${canonicalUrl}`) : 
    `${baseUrl}${currentPath}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      
      {/* Robots directives */}
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow"} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`} />
      <meta property="og:url" content={absoluteCanonicalUrl} />
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`} />
      
      {/* Canonical URL - ensuring it's always present and absolute */}
      <link rel="canonical" href={absoluteCanonicalUrl} />
      
      {/* Additional Meta Tags */}
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content={siteTitle} />
    </Helmet>
  );
};

export default SEOMetadata; 
